import React, { useState, useEffect, useCallback } from 'react';
import { employeeSecurityAPI } from '../../services/api';
import { toast } from '../../utils/toast';
import Icon from '../../ui/Icon';
import { formatInTz } from '../../utils/timezone';

const s = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 16, fontWeight: 800, color: '#1A202C' },
  sub: { fontSize: 12, color: '#718096', marginTop: 4 },
  btn: (v) => ({
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '8px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontSize: 12, fontWeight: 700, fontFamily: 'inherit',
    background: v === 'primary' ? 'var(--accent)' : v === 'danger' ? '#FEF2F2' : '#F1F5F9',
    color: v === 'primary' ? '#fff' : v === 'danger' ? '#EF4444' : '#374151',
  }),
  input: {
    width: '100%', padding: '9px 11px', border: '1.5px solid #E2E8F0', borderRadius: 8,
    fontSize: 13, outline: 'none', fontFamily: 'inherit', boxSizing: 'border-box',
    background: '#FAFAFA', color: '#1A202C',
  },
  label: {
    display: 'block', fontSize: 11, fontWeight: 700, color: '#6B7280',
    textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5,
  },
  th: {
    fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase',
    letterSpacing: '0.07em', padding: '10px 14px', borderBottom: '1px solid #f1f5f9',
    background: '#F8FAFF', textAlign: 'left',
  },
  td: {
    fontSize: 13, color: '#374151', padding: '11px 14px',
    borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle',
  },
  iconBtn: (v) => ({
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    width: 32, height: 32, padding: 0, borderRadius: 8, border: 'none', cursor: 'pointer',
    background: v === 'danger' ? '#FEF2F2' : '#EFF6FF',
    boxShadow: v === 'danger' ? 'inset 0 0 0 1px #FECACA' : 'inset 0 0 0 1px #BFDBFE',
    transition: 'background 0.15s, transform 0.1s',
  }),
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return formatInTz(iso, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch {
    return String(iso);
  }
}

function RecordModal({ initial, onSave, onClose }) {
  const [form, setForm] = useState({
    company_email: '',
    personal_email: '',
    phone_number: '',
    secret_key: '',
    ...initial,
  });
  const [saving, setSaving] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const handleSave = async () => {
    if (!form.company_email?.trim() || !form.personal_email?.trim()) {
      toast('Both emails are required', 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        company_email: form.company_email.trim(),
        personal_email: form.personal_email.trim(),
        phone_number: form.phone_number?.trim() || '',
        secret_key: form.secret_key?.trim() || '',
      };
      const result = initial?.id
        ? await employeeSecurityAPI.update(initial.id, payload)
        : await employeeSecurityAPI.create(payload);
      onSave(result);
    } catch (err) {
      toast(err.message || 'Failed to save record', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 480, padding: '28px 28px 24px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C' }}>
            {initial?.id ? 'Edit Employee Security Record' : 'Add Employee Security Record'}
          </div>
          <button type="button" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
            <Icon name="close" size={18} color="#9CA3AF" />
          </button>
        </div>
        <p style={{ fontSize: 12, color: '#718096', marginBottom: 20 }}>
          Map company email, personal email, phone, and secret key for account reset workflows.
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div>
            <label style={s.label}>Company Email *</label>
            <input
              type="email"
              style={s.input}
              value={form.company_email}
              onChange={e => setForm(f => ({ ...f, company_email: e.target.value }))}
              placeholder="name@company.com"
            />
          </div>
          <div>
            <label style={s.label}>Personal Email *</label>
            <input
              type="email"
              style={s.input}
              value={form.personal_email}
              onChange={e => setForm(f => ({ ...f, personal_email: e.target.value }))}
              placeholder="name@gmail.com"
            />
          </div>
          <div>
            <label style={s.label}>Phone Number</label>
            <input
              type="tel"
              style={s.input}
              value={form.phone_number || ''}
              onChange={e => setForm(f => ({ ...f, phone_number: e.target.value }))}
              placeholder="+65 9123 4567"
            />
          </div>
          <div>
            <label style={s.label}>Secret Key</label>
            <div style={{ display: 'flex', gap: 8 }}>
              <input
                type={showSecret ? 'text' : 'password'}
                style={{ ...s.input, flex: 1 }}
                value={form.secret_key || ''}
                onChange={e => setForm(f => ({ ...f, secret_key: e.target.value }))}
                placeholder="Verification / reset secret"
                autoComplete="new-password"
              />
              <button
                type="button"
                style={{ ...s.btn('default'), flexShrink: 0, padding: '8px 12px' }}
                onClick={() => setShowSecret(v => !v)}
              >
                {showSecret ? 'Hide' : 'Show'}
              </button>
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
          <button type="button" style={s.btn('default')} onClick={onClose}>Cancel</button>
          <button type="button" style={s.btn('primary')} onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ResetAdmin() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [modal, setModal] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await employeeSecurityAPI.list(search.trim() ? { search: search.trim() } : {});
      setRows(data || []);
    } catch (err) {
      toast(err.message || 'Failed to load records', 'error');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
  }, [load, search]);

  const handleDelete = async (id) => {
    try {
      await employeeSecurityAPI.delete(id);
      toast('Record deleted', 'success');
      setDeleteId(null);
      load();
    } catch (err) {
      toast(err.message || 'Delete failed', 'error');
    }
  };

  return (
    <div style={{ padding: '4px 0 8px', display: 'flex', flexDirection: 'column', minHeight: 0, flex: 1 }}>
      <div style={s.header}>
        <div>
          <div style={s.title}>Reset — Employee Security</div>
          <div style={s.sub}>Manage employee security records for password reset (email, phone, secret key).</div>
        </div>
        <button type="button" style={s.btn('primary')} onClick={() => setModal({})}>
          <Icon name="plus" size={14} color="#fff" />
          Add Record
        </button>
      </div>

      <div style={{ marginBottom: 16, maxWidth: 360 }}>
        <input
          type="search"
          style={s.input}
          placeholder="Search email or phone…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
      </div>

      <div style={{ background: '#fff', borderRadius: 10, border: '1px solid #e5e7eb', overflow: 'auto', flex: 1 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
          <thead>
            <tr>
              <th style={s.th}>Company Email</th>
              <th style={s.th}>Personal Email</th>
              <th style={s.th}>Phone Number</th>
              <th style={s.th}>Secret Key</th>
              <th style={s.th}>Created</th>
              <th style={s.th}>Updated</th>
              <th style={{ ...s.th, width: 88, textAlign: 'right' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#718096', padding: 32 }}>Loading…</td>
              </tr>
            )}
            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan={7} style={{ ...s.td, textAlign: 'center', color: '#718096', padding: 32 }}>
                  {search ? 'No records match your search.' : 'No records yet. Click Add Record to create one.'}
                </td>
              </tr>
            )}
            {!loading && rows.map(row => (
              <tr key={row.id}>
                <td style={{ ...s.td, fontWeight: 600, color: '#1A202C' }}>{row.company_email}</td>
                <td style={s.td}>{row.personal_email}</td>
                <td style={{ ...s.td, whiteSpace: 'nowrap' }}>{row.phone_number || '—'}</td>
                <td style={{ ...s.td, fontFamily: 'monospace', fontSize: 12, color: '#718096' }}>
                  {row.secret_key ? '••••••••' : '—'}
                </td>
                <td style={{ ...s.td, color: '#718096', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(row.created_at)}</td>
                <td style={{ ...s.td, color: '#718096', fontSize: 12, whiteSpace: 'nowrap' }}>{fmtDate(row.updated_at)}</td>
                <td style={{ ...s.td, textAlign: 'right' }}>
                  <div style={{ display: 'inline-flex', gap: 6, justifyContent: 'flex-end' }}>
                    <button
                      type="button"
                      style={s.iconBtn('edit')}
                      onClick={() => setModal(row)}
                      title="Edit record"
                      aria-label="Edit record"
                    >
                      <Icon name="edit" size={15} color="#2563eb" />
                    </button>
                    <button
                      type="button"
                      style={s.iconBtn('danger')}
                      onClick={() => setDeleteId(row.id)}
                      title="Delete record"
                      aria-label="Delete record"
                    >
                      <Icon name="trash" size={15} color="#EF4444" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <RecordModal
          initial={modal.id ? modal : null}
          onClose={() => setModal(null)}
          onSave={() => {
            setModal(null);
            toast(modal.id ? 'Record updated' : 'Record created', 'success');
            load();
          }}
        />
      )}

      {deleteId != null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 14, width: '100%', maxWidth: 400, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.15)' }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1A202C', marginBottom: 8 }}>Delete record?</div>
            <p style={{ fontSize: 13, color: '#718096', marginBottom: 20 }}>This cannot be undone.</p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button type="button" style={s.btn('default')} onClick={() => setDeleteId(null)}>Cancel</button>
              <button type="button" style={s.btn('danger')} onClick={() => handleDelete(deleteId)}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
